import React, { useState } from 'react'
import { Checkbox, Drawer, Tabs } from 'antd'
import Select from 'react-select'
import { useSnackbar } from 'notistack'
import Creatable from 'react-select/creatable'

import { useToggle, useListData, usePet, notifOps } from '../../modules'

import Confirm from '../reusable/confirm'
import SelectLabel from '../reusable/selectLabel'
import Datepicker from '../reusable/datePicker'
import SaleItemPricing from '../reusable/saleItemPricing'

const { TabPane } = Tabs

const NewInventoryItem = ({ toggle, store, string: str = null, visible, onPerform = () => null }) => {
    const [units, setUnits] = useState([])
    const [categories, setCategories] = useState([])

    const [name, setName] = useState(str)
    const [category, setCategory] = useState(null)
    const [receivedIn, setReceivedIn] = useState(null)
    const [issuedIn, setIssuedIn] = useState(null)
    const [accounts, setAccounts] = useState([])
    const [lists, setLists] = useState([])
    const [expense, setExpense] = useState()
    const [income, setIncome] = useState()
    const [qty, setQty] = useState(0)
    const [sp, setSp] = useState(0)
    const [rl, setRl] = useState(0)
    const [expiry, setExpiry] = useState()
    const [batchNo, setBatchNo] = useState('default')
    const [formulaOk, setOK] = useState(true)

    const [isSold, toggleSold] = useToggle(true)

    const { enqueueSnackbar } = useSnackbar()

    usePet('inventoryCategories', null, { notify: true, callback: ({ data }) => setCategories(data) })
    usePet('inventoryUnits', null, { notify: true, callback: ({ data }) => setUnits(data) })

    const [{ data: pData }] = usePet('priceLists', { limit: 100 }, { notify: true, callback: async ({ data }) => {
        let final = []

        for (let { name, _id, defaultPricingType, defaultFormula } of data.lists)
            if (defaultPricingType)
                final.push({
                    name,
                    _id,
                    price: 0,
                    formula: defaultFormula,
                    type: defaultPricingType ?? "Direct Price"
                })

        setLists(final)
    } })

    useListData('accounts', { types: ['Income', 'Expenses', 'Cost of Goods Sold'] }, { notify: true }, 'accounts', 100, ({ accounts: data }) => {
        let obj = {}

        data.forEach(acc => {
            if (!obj[acc.type])
                obj[acc.type] = []

            obj[acc.type].push({
                label: acc.name,
                value: acc._id
            })
        })

        let final = []
        for (let typ in obj) {
            final.push({
                label: typ,
                options: obj[typ]
            })
        }
        
        setAccounts(final)
    })

    const onComplete = ({ data }, notif, ops) => {
        onPerform(data)
        notif('Item has been added to inventory', ops('success'))

        setTimeout(toggle, 300)
    }

    const [{ loading }, execute] = usePet('newInventoryItemToStore', {
        name,
        category: category?.label,
        receivedIn: receivedIn?.label,
        issuedIn: issuedIn?.label,
        prices: lists,
        quantity: qty,
        batchNo,
        isSold,
        expiry: expiry?._d,
        income: income?.value,
        expense: expense?.value,
        store: store?.value ?? store?._id,
        reorderLevel: rl,
        sp,
        lists: lists.map(({ _id, name, price, formula, type }) => ({ 
            name, 
            price, 
            priceList: _id,
            pricingFormula: formula, 
            pricingType: type 
        }))
    }, { notify: true, callback: onComplete, manual: true, load: { msg: 'Adding item...' } })

    let types = ['Direct Price', 'Computed Price']

    const close = () => {
        toggle()
    }

    const editPrice = (vl, i) => {
        let temp = [...lists]

        if (!isNaN(vl) && vl !== null && vl !== undefined) {
            if (temp[i].name === 'Cash')
                setSp(vl)
            
            temp[i].price = vl
            setLists(temp)
        } else {
            enqueueSnackbar('Please enter the a numerical value', notifOps('warning'))
        }            
    }

    const editType = (vl, i) => {
        let temp = [...lists]
        temp[i].type = vl.label
        temp[i].price = 0
        temp[i].formula = ''
        setLists(temp)
        setSp(0)
    }

    const evaluate = vl => {
        let test = vl.replace('bp', 100)
        
        try {
            let resp = eval(test)
            if (isNaN(resp)) {
                setOK(false)
            } else {
                setOK(true)
            }
            console.log({ resp })
        } catch (error) {
            console.log({ error })
            setOK(false)
        }
    }
    console.log({formulaOk})

    const editFormula = (vl, i) => {
        let test = vl.replace('bp', 100)
        
        try {
            let resp = eval(test)
            if (isNaN(resp)) {
                enqueueSnackbar('Please enter a valid formula', notifOps('warning'))
            } else {
                let temp = [...lists]
                temp[i].formula = vl
                setLists(temp)
            }
        } catch (error) {
            enqueueSnackbar('Please enter a valid formula', notifOps('warning'))
        }
    }

    const modify = e => {
        switch (e.target.name) {
            case 'name':
                setName(e.target.value)
                break

            case 'quantity':
                setQty(e.target.value)
                break

            case 'batchNo':
                setBatchNo(e.target.value)
                break

            case 'sp':
                setLists(lists.map(list => list.name === 'Cash' ? { ...list, price: e.target.value } : list))
                setSp(e.target.value)
                break

            case 'rl':
                setRl(e.target.value)
                break

            default:
                break
        }
    }

    const save = () => formulaOk && Confirm({
        title: 'Save New Item?',
        message: 'Please confirm before proceeding?',
        okText: 'Proceed',
        onOk: execute
    })

    return (
        <Drawer
            closable
            maskClosable
            visible={ visible }
            onClose={ close }
            footer={
                <div className="w-full text-right">
                    <button disabled={ loading } onClick={ save } type="submit" className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save</button>
                </div>
            }
            width="30%">
            <p className="font-extrabold text-3xl mb-0">New Inventory Item</p>
            <Tabs>
                <TabPane key="0" tab="Item Details">
                    <div className="flex flex-wrap mt-3">
                        <div className="w-full px-3 mb-1">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Name
                            </label>
                            <input
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                id="grid-name"
                                type="text"
                                name="name"
                                value={name}
                                onChange={modify}
                                placeholder="Potatoes" />
                        </div>
                        <div className="w-full px-3 mb-4">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Category
                            </label>
                            <Creatable
                                value={category}
                                onCreateOption={val => {
                                    let temp = [...categories]
                                    temp.push(val)
                                    setCategories(temp)
                                    setCategory({ label: val, value: val })
                                }}
                                onChange={setCategory}
                                options={categories.map(category => ({ label: category, value: category }))}
                                placeholder="Select Category" />
                        </div>
                        <div className="w-full px-3 mb-4">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Received In
                            </label>
                            <Creatable
                                value={receivedIn}
                                onCreateOption={val => {
                                    let temp = [...units]
                                    temp.push(val)
                                    setUnits(temp)
                                    setReceivedIn({ label: val, value: val })
                                }}
                                onChange={setReceivedIn}
                                options={units.map(unit => ({ label: unit, value: unit }))}
                                placeholder="Search for Unit" />
                        </div>
                        <div className="w-full px-3 mb-4">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Issued In
                            </label>
                            <Creatable
                                value={issuedIn}
                                onCreateOption={val => {
                                    let temp = [...units]
                                    temp.push(val)
                                    setUnits(temp)
                                    setIssuedIn({ label: val, value: val })
                                }}
                                onChange={setIssuedIn}
                                options={units.map(unit => ({ label: unit, value: unit }))}
                                placeholder="Select Unit" />
                        </div>
                        <div className="w-full px-3 mb-4">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                                Purchase Account
                            </label>
                            <Select
                                className="mb-3"
                                value={expense}
                                onChange={setExpense}
                                formatGroupLabel={SelectLabel}
                                options={ accounts.filter(acc => acc.label !== 'Income') }
                                placeholder="Select account" />
                        </div>
                        <div className="w-full px-3 mb-1">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Initial Quantity
                            </label>
                            <input
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                id="grid-quantity"
                                type="number"
                                name="quantity"
                                onChange={modify}
                                value={qty}
                                placeholder="XXXX" />
                            <p className="text-gray-500 text-xs italic">Please indicate incase some are already in your inventory</p>
                        </div>
                        <div className="w-full px-3 mb-1">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Reorder Level
                            </label>
                            <input
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                id="grid-rl"
                                type="number"
                                name="rl"
                                onChange={modify}
                                value={rl}
                                placeholder="XXXX" />
                            <p className="text-gray-500 text-xs italic">This will be the redorder level for { store?.name }</p>
                        </div>
                        <div className="w-full px-3 mb-1">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Batch No.
                            </label>
                            <input
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                id="grid-quantity"
                                type="text"
                                name="batchNo"
                                onChange={modify}
                                value={batchNo}
                                placeholder="XXXX" />
                        </div>
                        <div className="w-full px-3 pb-3">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Expiry Date
                            </label>
                            <Datepicker
                                value={expiry}
                                onChange={setExpiry}
                                dateFormat="MM/DD/YYYY" />
                            <p className="text-gray-500 text-xs italic">Use incase current batch has expiry date</p>
                        </div>
                    </div>
                </TabPane>
                <TabPane key="1" tab="Sales">
                    <div className="w-full px-3 mb-1">
                        <Checkbox checked={ isSold } onChange={ toggleSold }>Is Sold Item</Checkbox>
                        <p className="text-gray-500 text-xs italic">Uncheck if this item will not be sold</p>
                    </div>
                    <div className="w-full px-3 mb-1">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                            Sales Account
                        </label>
                        <Select
                            className="mb-3"
                            isDisabled={ !isSold }
                            value={income}
                            onChange={setIncome}
                            formatGroupLabel={SelectLabel}
                            options={ accounts.filter(acc => acc.label === 'Income') }
                            placeholder="Select account" />
                    </div>
                    <div className="w-full px-3 mb-1">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-quantity">
                            Selling Price
                        </label>
                        <input
                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                            id="grid-sp"
                            type="number"
                            name="sp"
                            disabled={ !isSold }
                            onChange={modify}
                            value={sp}
                            placeholder="XXXX" />
                    </div>
                    {
                        isSold &&
                        <div className="w-full px-3 mb-1">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                                Price Lists
                            </label>
                            <SaleItemPricing
                                editFormula={ editFormula }
                                editPrice={ editPrice }
                                editType={ editType }
                                lists={ lists } 
                                evaluate={ evaluate } />
                        </div>
                    }
                </TabPane>
            </Tabs>
        </Drawer>
    )
}

export default NewInventoryItem